import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface StyledSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-styled-select',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative" [class.w-full]="fullWidth()">
      <button
        #trigger
        type="button"
        class="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-black text-slate-700 shadow-sm outline-none transition-all hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600"
        [class.w-full]="fullWidth()"
        [class.px-4]="density() === 'comfortable'"
        [class.py-3]="density() === 'comfortable'"
        [class.accent-focus]="true"
        [attr.id]="selectId"
        [attr.aria-label]="ariaLabel() || null"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-controls]="menuId"
        (click)="toggle()"
        (keydown)="onKeydown($event)"
      >
        <span class="truncate">{{ selectedOption()?.label || '' }}</span>
        <svg
          class="h-4 w-4 shrink-0 text-slate-400 transition-transform"
          [class.rotate-180]="isOpen()"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      @if (isOpen()) {
        <div
          [id]="menuId"
          role="listbox"
          class="absolute left-0 top-full z-50 mt-2 max-h-64 min-w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-2xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-800 dark:ring-white/5"
          [attr.aria-label]="ariaLabel() || null"
        >
          @for (option of options(); track option.value; let index = $index) {
            <button
              type="button"
              role="option"
              class="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-xs font-black transition-colors"
              [class.bg-slate-100]="highlightedIndex() === index"
              [class.dark:bg-slate-700]="highlightedIndex() === index"
              [class.text-slate-900]="selectedOption()?.value === option.value"
              [class.dark:text-white]="selectedOption()?.value === option.value"
              [class.text-slate-500]="selectedOption()?.value !== option.value"
              [class.dark:text-slate-300]="selectedOption()?.value !== option.value"
              [attr.aria-selected]="selectedOption()?.value === option.value"
              (mouseenter)="highlightedIndex.set(index)"
              (click)="selectOption(option.value)"
            >
              <span class="truncate">{{ option.label }}</span>
              @if (selectedOption()?.value === option.value) {
                <svg class="h-4 w-4 shrink-0 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m5 12 4 4L19 6" />
                </svg>
              }
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: [':host { display: block; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StyledSelectComponent {
  private static nextId = 0;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  readonly options = input<StyledSelectOption[]>([]);
  readonly value = input('');
  readonly ariaLabel = input('');
  readonly density = input<'compact' | 'comfortable'>('comfortable');
  readonly fullWidth = input(false);
  readonly valueChange = output<string>();

  readonly isOpen = signal(false);
  readonly highlightedIndex = signal(0);
  readonly selectId = `styled-select-${StyledSelectComponent.nextId++}`;
  readonly menuId = `${this.selectId}-menu`;

  readonly selectedOption = computed<StyledSelectOption | null>(() =>
    this.options().find(option => option.value === this.value()) ?? this.options()[0] ?? null,
  );

  toggle(): void {
    if (this.isOpen()) {
      this.close();
      return;
    }

    const selectedIndex = this.options().findIndex(option => option.value === this.value());
    this.highlightedIndex.set(selectedIndex >= 0 ? selectedIndex : 0);
    this.isOpen.set(true);
  }

  selectOption(value: string): void {
    this.valueChange.emit(value);
    this.close();
  }

  onKeydown(event: KeyboardEvent): void {
    const key = event.key;

    if (!this.isOpen()) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.toggle();
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      const direction = key === 'ArrowDown' ? 1 : -1;
      const optionsCount = this.options().length;
      if (optionsCount === 0) return;
      this.highlightedIndex.update(index => (index + direction + optionsCount) % optionsCount);
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const option = this.options()[this.highlightedIndex()];
      if (option) this.selectOption(option.value);
    }
  }

  close(): void {
    const wasOpen = this.isOpen();
    this.isOpen.set(false);
    if (wasOpen) this.trigger()?.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
